import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import cx from "classnames";
import _ from "lodash";
import Tooltip from "components/Tooltip";
import {
  applyDraftPageMeta,
  buildSchemaOutline,
  hashSchemaOutline,
  isAutoSchemaStale,
  parseSchemaMarkup,
  withPersistedAlias,
} from "pages/pages/helper/schemaMarkup";
import { generateSchemaMarkup, invalidateSchemaMarkup } from "actions/pages";

import styles from "./schemaMarkupField.module.scss";

const EMPTY_WIDGETS = [];
const EMPTY_ALIASES = [];

export const SchemaMarkupField = ({ editorSession, page, value, seo, onChange }) => {
  const dispatch = useDispatch();
  const { id: routePageId } = useParams();
  const isPageWidget = Boolean(_.get(page, "uid"));
  const pageId = isPageWidget ? routePageId : (_.get(page, "id") || routePageId);
  const rootUid = isPageWidget ? _.get(page, "uid") : null;

  const widgets = useSelector(
    (state) =>
      _.get(state, `${editorSession}.present.widgets`) || EMPTY_WIDGETS
  );

  const pageEntity = useSelector((state) =>
    _.get(state, `pages.page[${pageId}].item`)
  );

  const schemaMarkupState = useSelector((state) =>
    _.get(state, `pages.page[${pageId}].schemaMarkup`, {})
  );
  const generating = _.get(schemaMarkupState, "generating", false);
  const generated = _.get(schemaMarkupState, "generated", false);
  const generateError = _.get(schemaMarkupState, "error");
  const generatedItem = _.get(schemaMarkupState, "item");

  const aliases = useSelector(
    (state) => _.get(state, "aliases.items") || EMPTY_ALIASES
  );
  const persistedAlias = useSelector((state) =>
    _.get(state, "aliases.alias.item")
  );
  const resolvedAliases = withPersistedAlias(aliases, persistedAlias);

  const pageName =
    _.get(value, "info.name") || _.get(page, "name") || _.get(pageEntity, "name");
  const draftPage = applyDraftPageMeta(pageEntity || page, {
    name: pageName,
    title: _.get(seo, "name") || pageName,
    description: _.get(seo, "description") || "",
  });

  const stored = _.get(seo, "schemaMarkup") || "";
  const source = _.get(seo, "schemaMarkupSource");
  const isAuto = source === "auto" && Boolean(stored);
  const isStale = isAutoSchemaStale(
    seo,
    draftPage,
    widgets,
    rootUid,
    resolvedAliases
  );

  const [draft, setDraft] = useState(stored);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(stored);
    setError("");
  }, [stored]);

  useEffect(() => {
    if (!generated || !generatedItem) {
      return;
    }

    setDraft(generatedItem.schemaMarkup);
    setError("");
    onChange({
      schemaMarkup: generatedItem.schemaMarkup,
      schemaMarkupSource: "auto",
      schemaContentHash: generatedItem.schemaContentHash,
    });
    dispatch(invalidateSchemaMarkup(pageId));
  }, [generated, generatedItem]);

  useEffect(() => {
    if (!generateError) {
      return;
    }

    setError("Schema markup could not be generated. Please try again.");
    dispatch(invalidateSchemaMarkup(pageId));
  }, [generateError]);

  const commit = (markup, markupSource, contentHash) => {
    onChange({
      schemaMarkup: markup,
      schemaMarkupSource: markupSource,
      schemaContentHash: contentHash,
    });
  };

  const handleBlur = () => {
    const trimmed = draft.trim();

    if (trimmed === stored.trim()) {
      return;
    }

    if (!trimmed) {
      setError("");
      commit("", "auto", "");
      return;
    }

    try {
      const formatted = JSON.stringify(parseSchemaMarkup(trimmed), null, 2);
      setDraft(formatted);
      setError("");
      commit(formatted, "manual", "");
    } catch (caught) {
      setError(_.get(caught, "message") || "Enter valid JSON-LD.");
    }
  };

  const handleGenerate = () => {
    if (!pageId || generating) return;

    const outline = buildSchemaOutline(
      draftPage,
      widgets,
      rootUid,
      resolvedAliases
    );

    setError("");
    dispatch(
      generateSchemaMarkup(pageId, {
        content: outline,
        contentHash: hashSchemaOutline(outline),
      })
    );
  };

  const handleReset = () => {
    setDraft("");
    setError("");
    commit("", "auto", "");
  };

  return (
    <Form.Group controlId="schemaMarkup" className={styles.group}>
      <div className={styles.labelRow}>
        <Form.Label className={styles.label}>Schema Markup (JSON-LD)</Form.Label>
        <span className={styles.status}>
          {source === "manual" ? "Custom" : stored ? "Auto-generated" : "Not generated"}
          {isAuto && (
            <Tooltip
              placement="top"
              content="Schema generated by AI. It may contain errors or inaccuracies and should be verified before publishing."
            >
              <span className={styles.infoIcon} aria-label="AI schema information">
                <i className="cleverstory-info" />
              </span>
            </Tooltip>
          )}
        </span>
      </div>
      <Form.Control
        as="textarea"
        className={cx(styles.control, styles.textarea, {
          [styles.textareaError]: Boolean(error),
        })}
        name="schemaMarkup"
        placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "WebPage"\n}'}
        value={draft}
        spellCheck={false}
        onChange={(e) => {
          setDraft(e.target.value);
        }}
        onBlur={handleBlur}
      />
      {error && <span className={styles.error}>{error}</span>}
      {isStale && (
        <span className={styles.hint}>
          Page content has changed. Generate the updated schema before publishing.
        </span>
      )}
      <div className={styles.actions}>
        {source === "manual" && (
          <Button variant="secondary" onClick={handleReset}>
            Clear
          </Button>
        )}
        <button
          type="button"
          className={styles.generate}
          disabled={!pageId || generating || _.isEmpty(widgets)}
          onClick={handleGenerate}
        >
          <i className="cleverstory-text-ai" />
          <span>{generating ? "Generating..." : "Generate with AI"}</span>
        </button>
      </div>
    </Form.Group>
  );
};
